<template>
  <div class="etq-palette">
    <div class="etq-panel-heading">
      <div>
        <span class="etq-panel-kicker">Conteúdo</span>
        <h5>O que vai na etiqueta</h5>
      </div>
      <span class="badge badge-light-primary">{{ elements.length }}</span>
    </div>

    <section class="palette-section">
      <div class="section-heading">
        <div>
          <strong>Na etiqueta</strong>
          <small>Clique para localizar e ajustar.</small>
        </div>
      </div>

      <div v-if="!elements.length" class="etq-palette-empty compact">
        <i class="far fa-square"></i>
        <span>A etiqueta ainda está vazia.</span>
      </div>

      <button
        v-for="element in orderedElements"
        :key="element.id"
        type="button"
        class="current-element"
        :class="{ selected: selectedId === element.id }"
        @click="$emit('select', element.id)"
      >
        <span class="item-icon"><i :class="elementIcon(element)"></i></span>
        <span class="item-copy">
          <strong>{{ elementLabel(element) }}</strong>
          <small>{{ elementKind(element) }}</small>
        </span>
        <span class="element-status" :class="elementStatus(element).className">
          {{ elementStatus(element).label }}
        </span>
      </button>
    </section>

    <section class="palette-section available-section">
      <div class="section-heading">
        <div>
          <strong>Disponível neste teste</strong>
          <small>Segue as opções ligadas na aba Exemplo.</small>
        </div>
      </div>

      <div class="preset-list">
        <button
          v-for="preset in availablePresets"
          :key="preset.key"
          type="button"
          class="preset-item"
          :class="{ present: Boolean(existingElement(preset)) }"
          :disabled="preset.disabled && !existingElement(preset)"
          @click="usePreset(preset)"
        >
          <span class="item-icon"><i :class="preset.icon"></i></span>
          <span class="item-copy">
            <strong>{{ preset.label }}</strong>
            <small>{{ preset.helper }}</small>
          </span>
          <span v-if="existingElement(preset)" class="preset-action">
            Ajustar <i class="fas fa-crosshairs"></i>
          </span>
          <span v-else-if="preset.disabled" class="preset-action disabled">
            Preencha em Exemplo
          </span>
          <span v-else class="preset-action">
            Adicionar <i class="fas fa-plus"></i>
          </span>
        </button>
      </div>
    </section>

    <section class="palette-section fixed-section">
      <button
        type="button"
        class="section-toggle fixed-toggle"
        :aria-expanded="fixedOpen"
        @click="fixedOpen = !fixedOpen"
      >
        <span>
          <strong>Outras informações fixas</strong>
          <small
            >Depois de adicionadas, fazem parte deste modelo e não dependem das
            opções da impressão.</small
          >
        </span>
        <i :class="fixedOpen ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
      </button>

      <div v-if="fixedOpen" class="fixed-content">
        <div class="input-icon fixed-search">
          <input
            v-model.trim="fixedSearch"
            type="search"
            class="form-control form-control-sm"
            placeholder="Buscar NCM, lote, validade, empresa..."
            aria-label="Buscar outras informações fixas"
          />
          <span><i class="flaticon2-search-1 text-muted"></i></span>
        </div>

        <div v-if="!groupedFixedFields.length" class="etq-palette-empty">
          <i class="flaticon2-search-1"></i>
          <span>Nenhuma informação encontrada.</span>
        </div>

        <div
          v-for="group in groupedFixedFields"
          :key="group.category"
          class="fixed-group"
        >
          <div class="fixed-group-title">
            <span>{{ group.category }}</span>
            <small>{{ group.items.length }}</small>
          </div>
          <button
            v-for="fieldItem in group.items"
            :key="fieldItem.key"
            type="button"
            class="fixed-field"
            :class="{ present: Boolean(existingFixedElement(fieldItem)) }"
            @click="useFixedField(fieldItem)"
          >
            <span class="item-icon"
              ><i :class="fixedFieldIcon(fieldItem)"></i
            ></span>
            <span class="item-copy">
              <strong>{{ fieldItem.label }}</strong>
              <small>{{ fixedFieldSample(fieldItem) }}</small>
            </span>
            <span class="preset-action">
              <template v-if="existingFixedElement(fieldItem)">
                Ajustar <i class="fas fa-crosshairs"></i>
              </template>
              <template v-else>
                Adicionar <i class="fas fa-plus"></i>
              </template>
            </span>
          </button>
        </div>
      </div>
    </section>

    <section class="palette-section support-section">
      <button
        type="button"
        class="section-toggle support-toggle"
        :aria-expanded="supportOpen"
        @click="supportOpen = !supportOpen"
      >
        <span>
          <strong>Apoio visual</strong>
          <small>Texto livre, QR Code, linha e caixa.</small>
        </span>
        <i
          :class="supportOpen ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"
        ></i>
      </button>

      <div v-if="supportOpen" class="etq-quick-grid">
        <button type="button" class="etq-quick" @click="$emit('add', 'text')">
          <i class="fas fa-font"></i><span>Texto livre</span>
        </button>
        <button type="button" class="etq-quick" @click="$emit('add', 'qrcode')">
          <i class="fas fa-qrcode"></i><span>QR Code</span>
        </button>
        <button type="button" class="etq-quick" @click="$emit('add', 'line')">
          <i class="fas fa-minus"></i><span>Linha</span>
        </button>
        <button
          type="button"
          class="etq-quick"
          @click="$emit('add', 'rectangle')"
        >
          <i class="far fa-square"></i><span>Caixa</span>
        </button>
      </div>
    </section>
  </div>
</template>

<script>
import { formatFieldSample } from "@/modules/Etiquetas/services/etiquetaDefinition";

const ruleOption = {
  quando_preco: "preco",
  quando_codigo_barras: "cod_barras",
  quando_referencia: "considerar_referencia",
  quando_codigo_interno: "considerar_codigo_interno",
  quando_valor_kg: "valor_por_kg",
  quando_logo: "imprimir_logo",
};

const MAIN_AND_TECHNICAL_FIELD_KEYS = new Set([
  "produto.descricao",
  "produto.descricao_complementar",
  "produto.codigo_barras_etiqueta",
  "produto.codigo_legenda_etiqueta",
  "produto.codigo_barras_a4054",
  "produto.referencia_preco",
  "produto.preco",
  "produto.preco_kg",
  "impressao.texto_personalizado",
]);

const fallbackFields = {
  "produto.descricao": {
    key: "produto.descricao",
    label: "Descrição do produto",
    format: "texto",
    display_rule: "se_preenchido",
  },
  "produto.descricao_complementar": {
    key: "produto.descricao_complementar",
    label: "Descrição complementar",
    format: "texto",
    display_rule: "se_preenchido",
  },
  "produto.codigo_barras_etiqueta": {
    key: "produto.codigo_barras_etiqueta",
    label: "Código escolhido para a etiqueta",
    format: "texto",
    display_rule: "se_preenchido",
  },
  "produto.preco": {
    key: "produto.preco",
    label: "Preço de venda",
    format: "moeda",
    display_rule: "quando_preco",
  },
  "produto.preco_kg": {
    key: "produto.preco_kg",
    label: "Preço por KG",
    format: "moeda",
    display_rule: "quando_valor_kg",
  },
  "impressao.texto_personalizado": {
    key: "impressao.texto_personalizado",
    label: "Texto informado na impressão",
    format: "texto",
    display_rule: "se_preenchido",
  },
};

export default {
  name: "EtiquetaFieldPalette",
  props: {
    fields: {
      type: Array,
      default: () => [],
    },
    printOptions: {
      type: Object,
      default: () => ({}),
    },
    customText: {
      type: String,
      default: "",
    },
    elements: {
      type: Array,
      default: () => [],
    },
    selectedId: {
      type: String,
      default: null,
    },
    convertedLegacy: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      supportOpen: false,
      fixedOpen: false,
      fixedSearch: "",
    };
  },
  computed: {
    orderedElements() {
      return [...this.elements].sort(
        (first, second) => Number(first.ordem || 0) - Number(second.ordem || 0)
      );
    },
    activeCodeLabels() {
      const labels = [];
      if (this.printOptions.considerar_referencia) labels.push("referência");
      if (this.printOptions.cod_barras) labels.push("EAN");
      if (this.printOptions.considerar_codigo_interno) {
        labels.push(
          this.printOptions.codigo_interno_somente_numeracao
            ? "código interno em número"
            : "código interno"
        );
      }
      return labels;
    },
    availablePresets() {
      const presets = [
        this.fieldPreset(
          "descricao",
          "Descrição do produto",
          "produto.descricao",
          "fas fa-align-left",
          "Informação principal do produto."
        ),
        this.fieldPreset(
          "descricao_complementar",
          "Descrição complementar",
          "produto.descricao_complementar",
          "fas fa-align-left",
          "Detalhes adicionais do produto."
        ),
      ];

      if (this.activeCodeLabels.length) {
        presets.push({
          key: "codigo_dinamico",
          label: "Código para impressão",
          helper: `Será impresso: ${this.activeCodeLabels.join(" + ")}.`,
          icon: "fas fa-barcode",
          type: "barcode",
          field: this.field("produto.codigo_barras_etiqueta"),
          match: (element) => this.isScenarioBarcode(element),
        });
      }
      if (this.printOptions.preco) {
        presets.push(
          this.fieldPreset(
            "preco",
            "Preço de venda",
            "produto.preco",
            "fas fa-dollar-sign",
            'Será impresso quando "Preço" estiver ligado.',
            ["produto.preco", "produto.referencia_preco"]
          )
        );
      }
      if (this.printOptions.valor_por_kg) {
        presets.push(
          this.fieldPreset(
            "preco_kg",
            "Preço por KG",
            "produto.preco_kg",
            "fas fa-weight-hanging",
            'Será impresso quando "Valor por KG" estiver ligado.'
          )
        );
      }
      if (this.printOptions.imprimir_logo) {
        presets.push({
          key: "logo",
          label: "Logo da empresa",
          helper: 'Será impressa quando "Logo" estiver ligada.',
          icon: "far fa-image",
          type: "logo",
          field: null,
          match: (element) => element.tipo === "logo",
        });
      }

      presets.push({
        ...this.fieldPreset(
          "texto_impressao",
          "Texto informado na impressão",
          "impressao.texto_personalizado",
          "fas fa-comment-alt",
          this.customText.trim()
            ? `Conteúdo do teste: “${this.customText.trim()}”`
            : "Preencha o texto personalizado na aba Exemplo."
        ),
        disabled: !this.customText.trim(),
      });

      return presets;
    },
    fixedFields() {
      const search = this.normalize(this.fixedSearch);
      return this.fields.filter((field) => {
        if (MAIN_AND_TECHNICAL_FIELD_KEYS.has(field.key)) return false;
        if (!search) return true;
        return this.normalize(
          `${field.label || ""} ${field.category || ""} ${field.key || ""}`
        ).includes(search);
      });
    },
    groupedFixedFields() {
      const groups = this.fixedFields.reduce((result, field) => {
        const category = field.category || "Outras informações";
        if (!result[category]) result[category] = [];
        result[category].push(field);
        return result;
      }, {});
      return Object.keys(groups).map((category) => ({
        category,
        items: groups[category],
      }));
    },
  },
  methods: {
    field(key) {
      return (
        this.fields.find((candidate) => candidate.key === key) ||
        fallbackFields[key]
      );
    },
    fieldPreset(key, label, fieldKey, icon, helper, matchKeys = [fieldKey]) {
      return {
        key,
        label,
        helper,
        icon,
        type: "field",
        field: this.field(fieldKey),
        match: (element) =>
          element.tipo === "field" && matchKeys.includes(element.campo),
      };
    },
    existingElement(preset) {
      return this.elements.find((element) => preset.match(element)) || null;
    },
    usePreset(preset) {
      const existing = this.existingElement(preset);
      if (existing) {
        this.$emit("select", existing.id);
        return;
      }
      if (!preset.disabled) this.$emit("add", preset.type, preset.field);
    },
    elementLabel(element) {
      return element.nome || "Elemento sem nome";
    },
    elementKind(element) {
      if (element.tipo === "field") {
        return this.field(element.campo)?.label || element.campo || "Campo";
      }
      return (
        {
          text: "Texto livre",
          barcode: this.isScenarioBarcode(element)
            ? "Código conforme as opções do teste"
            : "Código de barras fixo do modelo",
          qrcode: "QR Code",
          logo: "Logo da empresa",
          line: "Linha de apoio",
          rectangle: "Caixa / borda",
        }[element.tipo] || "Elemento visual"
      );
    },
    elementIcon(element) {
      return (
        {
          text: "fas fa-font",
          barcode: "fas fa-barcode",
          qrcode: "fas fa-qrcode",
          logo: "far fa-image",
          line: "fas fa-minus",
          rectangle: "far fa-square",
        }[element.tipo] || "fas fa-align-left"
      );
    },
    elementStatus(element) {
      if (element.visivel === false) {
        return { label: "Oculto", className: "is-off" };
      }
      const option = ruleOption[element.exibir_quando];
      if (option && !this.printOptions[option]) {
        return { label: "Opção desligada", className: "is-off" };
      }
      if (this.isScenarioBarcode(element) && !this.activeCodeLabels.length) {
        return { label: "Sem código no teste", className: "is-off" };
      }
      return { label: "Configurado", className: "is-on" };
    },
    isScenarioBarcode(element) {
      if (element?.tipo !== "barcode") return false;
      if (
        [
          "produto.codigo_barras_etiqueta",
          "produto.codigo_barras_a4054",
        ].includes(element.campo)
      ) {
        return true;
      }
      return (
        element.campo === "produto.codigo_barras" &&
        (this.convertedLegacy ||
          element.exibir_quando === "quando_codigo_barras")
      );
    },
    normalize(value) {
      return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
    },
    existingFixedElement(field) {
      return (
        this.elements.find(
          (element) =>
            element.tipo === "field" &&
            element.campo === field.key &&
            ["se_preenchido", "sempre"].includes(
              element.exibir_quando || "se_preenchido"
            )
        ) || null
      );
    },
    useFixedField(field) {
      const existing = this.existingFixedElement(field);
      if (existing) {
        this.$emit("select", existing.id);
        return;
      }
      this.$emit("add", "field", {
        ...field,
        display_rule: "se_preenchido",
      });
    },
    fixedFieldSample(field) {
      return formatFieldSample(field);
    },
    fixedFieldIcon(field) {
      return (
        {
          moeda: "fas fa-dollar-sign",
          data: "far fa-calendar-alt",
          numero: "fas fa-hashtag",
        }[field.format] || "fas fa-font"
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.etq-palette {
  padding: 18px;
}

.etq-panel-heading,
.section-heading,
.section-toggle,
.support-toggle {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.etq-panel-heading {
  margin-bottom: 14px;

  h5 {
    margin: 2px 0 0;
    font-weight: 700;
    color: #263238;
  }
}

.etq-panel-kicker {
  color: #7e8299;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.palette-section {
  padding: 14px 0;
  border-top: 1px solid #eef0f5;
}

.palette-section:first-of-type {
  border-top: 0;
  padding-top: 0;
}

.section-heading {
  margin-bottom: 9px;

  strong,
  small {
    display: block;
  }

  strong {
    color: #3f4254;
    font-size: 12px;
  }

  small {
    color: #a1a5b7;
    font-size: 10px;
    margin-top: 2px;
  }
}

.current-element,
.preset-item {
  width: 100%;
  min-height: 48px;
  margin-bottom: 6px;
  padding: 7px 8px;
  border: 1px solid transparent;
  border-radius: 9px;
  background: #f8fafc;
  display: flex;
  align-items: center;
  text-align: left;
  transition: 0.15s ease;

  &:hover,
  &:focus,
  &.selected {
    border-color: #b79afa;
    background: #f5f0ff;
    outline: none;
  }
}

.fixed-field {
  width: 100%;
  min-height: 46px;
  margin-bottom: 5px;
  padding: 7px 8px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: transparent;
  display: flex;
  align-items: center;
  text-align: left;

  &:hover,
  &:focus {
    border-color: #d8cafa;
    background: #f7f3ff;
    outline: none;
  }

  &.present {
    border-color: #b9e7da;
    background: #f2fbf8;
  }
}

.preset-item {
  background: #fff;
  border-color: #e4e6ef;

  &.present {
    border-color: #b9e7da;
    background: #f2fbf8;
  }

  &:disabled {
    opacity: 0.58;
    cursor: not-allowed;
  }
}

.item-icon {
  width: 30px;
  height: 30px;
  flex: 0 0 30px;
  margin-right: 8px;
  border-radius: 7px;
  background: #eee8ff;
  color: #8950fc;
  display: grid;
  place-items: center;
}

.item-copy {
  min-width: 0;
  flex: 1;

  strong,
  small {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  strong {
    color: #3f4254;
    font-size: 11px;
  }

  small {
    margin-top: 2px;
    color: #7e8299;
    font-size: 9px;
    line-height: 1.3;
  }
}

.element-status,
.preset-action {
  flex: 0 0 auto;
  margin-left: 7px;
  font-size: 9px;
  font-weight: 700;
  text-align: right;
}

.element-status {
  padding: 3px 6px;
  border-radius: 999px;

  &.is-on {
    color: #13795b;
    background: #def7ec;
  }

  &.is-off {
    color: #8a6512;
    background: #fff4cc;
  }
}

.preset-action {
  color: #8950fc;

  &.disabled {
    max-width: 58px;
    color: #7e8299;
    line-height: 1.15;
  }
}

.section-toggle {
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  color: #3f4254;
  text-align: left;

  strong,
  small {
    display: block;
  }

  strong {
    font-size: 12px;
  }

  small {
    margin-top: 2px;
    color: #a1a5b7;
    font-size: 10px;
  }

  > i {
    margin-top: 4px;
    color: #b5b5c3;
    font-size: 10px;
  }
}

.fixed-content {
  margin-top: 12px;
}

.fixed-search {
  margin-bottom: 10px;
}

.fixed-group + .fixed-group {
  margin-top: 10px;
}

.fixed-group-title {
  padding: 4px 7px;
  color: #7e8299;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;

  small {
    color: #b5b5c3;
    font-size: 9px;
  }
}

.etq-quick-grid {
  margin-top: 10px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 7px;
}

.etq-quick {
  min-height: 44px;
  padding: 6px;
  border: 1px solid #e4e6ef;
  border-radius: 8px;
  background: #fff;
  color: #5e6278;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 10px;
  font-weight: 600;

  i {
    color: #8950fc;
  }

  &:hover,
  &:focus {
    border-color: #8950fc;
    background: #f7f3ff;
    outline: none;
  }
}

.etq-palette-empty {
  color: #a1a5b7;
  display: flex;
  align-items: center;
  padding: 10px 4px;
  gap: 7px;
  font-size: 10px;
}
</style>
